import { Ban, Check, Eye, Mail, Megaphone, MessageSquareWarning, Send, TriangleAlert, Undo2 } from "lucide-react";
import { useState, type FormEvent } from "react";

import { EmptyState } from "../../components/EmptyState";
import { ErrorState } from "../../components/ErrorState";
import { trpc, type RouterOutputs } from "../../lib/trpc";

type Azienda = RouterOutputs["platform"]["companies"][number];

function quando(data: Date | string) {
  return new Date(data).toLocaleString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function esitoLeggibile(outcome: string, dettaglio: string | null) {
  if (outcome === "delivered") return "Consegnata al destinatario.";
  if (outcome === "opened") return "Consegnata e aperta.";
  if (outcome === "bounced") return `Tornata indietro${dettaglio ? `: ${dettaglio}` : "."}`;
  if (outcome === "rejected") return `Rifiutata dal destinatario${dettaglio ? `: ${dettaglio}` : "."}`;
  return "Partita, esito non ancora comunicato.";
}

function etichettaEmail(kind: string) {
  if (kind === "purchase_order") return "Ordine al fornitore";
  if (kind === "material_request") return "Richiesta materiali";
  if (kind === "support") return "Richiesta urgente";
  return "Segnalazione";
}

function etichettaSegnale(segnale: { source: string; kind?: string | null; context?: string | null }) {
  if (segnale.source === "error") return `Errore in ${segnale.context ?? "un punto non registrato"}`;
  if (segnale.kind === "issue") return "Problema segnalato";
  if (segnale.kind === "change") return "Modifica chiesta";
  return "Idea nuova";
}

/**
 * I messaggi di **una** azienda, dentro il suo quadrato in console.
 *
 * Prima queste righe vivevano in un elenco unico, sotto le aziende, con un filtro a tendina
 * per scegliere di chi si stava parlando. Funzionava, ma costringeva a tenere a mente quale
 * azienda si stava guardando mentre si scorreva un elenco di tutte: la domanda vera, davanti
 * a un cliente al telefono, e sempre "cosa c'e fra me e **questa** azienda".
 *
 * Le due direzioni stanno insieme di proposito, in ordine di lettura: prima cosa hanno
 * scritto loro, che e quello che aspetta una risposta, poi cosa e stato mandato a loro.
 *
 * I dati si chiedono solo quando il pannello e aperto: un elenco di venti aziende non deve
 * fare quaranta chiamate per mostrare venti quadrati chiusi.
 */
export function MessaggiAzienda({ azienda }: { azienda: Azienda }) {
  const utils = trpc.useUtils();
  const [confermeAperte, setConfermeAperte] = useState<string | null>(null);
  // A chi va il messaggio dentro questa azienda: tutti, oppure una persona sola.
  const [aChi, setAChi] = useState("");
  /**
   * Lo stesso messaggio anche a tutte le altre aziende.
   *
   * Resta una spunta dentro questa sezione, e non diventa un pannello a parte: chi scrive
   * sta gia parlando con una azienda, e decidere di dirlo anche alle altre e un ripensamento
   * di un attimo. Un pannello separato lo trasformerebbe in un cambio di schermata, cioe in
   * una decisione diversa da quella che si sta prendendo.
   */
  const [ancheAlleAltre, setAncheAlleAltre] = useState(false);
  const [esito, setEsito] = useState<string | null>(null);
  const [postaAperta, setPostaAperta] = useState<string | null>(null);
  const segnali = trpc.platform.signals.useQuery({ companyId: azienda.id, limit: 50 });
  const messaggi = trpc.platform.messaggi.useQuery({ companyId: azienda.id, limit: 20 });
  const segna = trpc.platform.segnaSegnalazione.useMutation({
    onSuccess: () => {
      void utils.platform.signals.invalidate();
      // Il numero sul quadrato chiuso vive nell'elenco aziende: senza questo resterebbe
      // fermo sul conto di prima fino al prossimo caricamento della pagina.
      void utils.platform.companies.invalidate();
    },
  });

  const ritira = trpc.platform.ritiraMessaggio.useMutation({
    onSuccess: () => void utils.platform.messaggi.invalidate(),
  });
  const conferme = trpc.platform.conferme.useQuery(
    { announcementId: confermeAperte ?? "" },
    { enabled: Boolean(confermeAperte) },
  );
  const persone = trpc.platform.people.useQuery({ companyId: azienda.id });
  const posta = trpc.platform.emailDeliveries.useQuery({ companyId: azienda.id, limit: 20 });
  const invia = trpc.platform.inviaMessaggio.useMutation({
    onSuccess: () => {
      void utils.platform.messaggi.invalidate();
      void utils.platform.audit.invalidate();
    },
  });

  async function manda(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    const modulo = evento.currentTarget;
    const dati = new FormData(modulo);
    setEsito(null);
    try {
      const risposta = await invia.mutateAsync({
        // Il flag vince su tutto: se il messaggio va a tutte le aziende, dentro questa non
        // c'e piu una persona da scegliere.
        target: ancheAlleAltre ? "all" : aChi ? "user" : "company",
        companyId: ancheAlleAltre || aChi ? undefined : azienda.id,
        userId: !ancheAlleAltre && aChi ? aChi : undefined,
        title: String(dati.get("title")),
        body: String(dati.get("body")),
      });
      modulo.reset();
      setAChi("");
      setAncheAlleAltre(false);
      setEsito(`Mandato a ${risposta.destinatari} ${risposta.destinatari === 1 ? "persona" : "persone"}.`);
    } catch {
      // L'errore lo racconta gia la mutazione, qui basta non lasciare l'esito di prima.
      setEsito(null);
    }
  }

  // Il server filtra gia per azienda; qui si filtra una seconda volta perche questo pannello
  // porta il nome di una azienda sola e non deve poter mostrare la riga di un'altra.
  const suoi = (segnali.data ?? []).filter((segnale) => segnale.companyId === azienda.id);
  const inviati = messaggi.data ?? [];

  return (
    <div className="azienda-messaggi">
      <section className="azienda-messaggi__blocco" aria-label={`Scrivi a ${azienda.name}`}>
        <h4>Scrivi a loro</h4>
        <form className="console-form azienda-messaggi__scrivi" onSubmit={manda}>
          {!ancheAlleAltre && (
            <label>
              <span>A chi</span>
              <select value={aChi} onChange={(evento) => setAChi(evento.target.value)}>
                <option value="">Tutta l'azienda</option>
                {(persone.data ?? []).map((persona) => (
                  <option key={persona.userId} value={persona.userId}>
                    {persona.name} ({persona.role === "owner" ? "titolare" : persona.role === "manager" ? "responsabile" : "collaboratore"})
                  </option>
                ))}
              </select>
            </label>
          )}
          <label>
            <span>Titolo</span>
            <input name="title" required maxLength={120} placeholder="Da oggi c'e la Settimana" />
          </label>
          <label className="form-grid__wide">
            <span>Messaggio</span>
            <textarea name="body" rows={3} required maxLength={4000} placeholder="Trovate la nuova voce nel menu." />
          </label>
          <label className="check-control azienda-messaggi__tutte">
            <input
              type="checkbox"
              checked={ancheAlleAltre}
              onChange={(evento) => setAncheAlleAltre(evento.target.checked)}
            />{" "}
            Manda lo stesso messaggio anche a tutte le altre aziende
          </label>
          {ancheAlleAltre && (
            <p className="console-nota">
              Parte un avviso solo, uguale per tutti, e comparira dentro il quadrato di ogni azienda.
            </p>
          )}
          {invia.error && <p className="form-error">{invia.error.message}</p>}
          {esito && <p className="support-form__ok" role="status">{esito}</p>}
          <div className="form-actions">
            <button className={`button button--primary${invia.isPending ? " button--saving" : ""}`} disabled={invia.isPending}>
              <Send size={17} aria-hidden="true" /> {invia.isPending ? "Invio..." : "Invia il messaggio"}
            </button>
          </div>
        </form>
      </section>

      <section className="azienda-messaggi__blocco" aria-label={`Scritti da ${azienda.name}`}>
        <h4>Cosa hanno scritto loro</h4>
        {segnali.isLoading && <p className="loading-copy">Caricamento...</p>}
        {/* Prima l'errore, poi il vuoto, e il vuoto solo con una risposta in mano.
            Ogni blocco guarda **la propria** lettura: prima queste righe chiedevano a una
            somma delle due, e una segnalazione gia caduta restava nascosta sotto
            "Caricamento..." finche l'altra lettura non aveva finito.
            La domanda giusta non e "sto caricando?" ma "ho ricevuto una risposta?": fra un
            tentativo e l'altro la lettura non carica e non ha ancora fallito, e in quella
            finestra "non c'e niente" e una risposta inventata. */}
        {!segnali.isLoading && segnali.error && (
          <ErrorState cosa="le segnalazioni" dettaglio={segnali.error.message} onRiprova={() => void segnali.refetch()} />
        )}
        {segnali.isSuccess && !segnali.error && suoi.length === 0 && (
          <EmptyState title="Niente da leggere">
            Da questa azienda non e arrivata nessuna segnalazione e non si e rotto niente.
          </EmptyState>
        )}
        <ul className="segnali-elenco">
          {suoi.map((segnale) => (
            <li className={`segnale segnale--${segnale.source}`} key={`${segnale.source}-${segnale.id}`}>
              <div className="segnale__testa">
                {segnale.source === "error"
                  ? <TriangleAlert size={16} aria-hidden="true" />
                  : <MessageSquareWarning size={16} aria-hidden="true" />}
                <strong>{etichettaSegnale(segnale)}</strong>
                <small>{quando(segnale.occurredAt)}</small>
              </div>
              <p>{segnale.message}</p>
              {segnale.personName && <small>{segnale.personName}</small>}
              {/* La spunta vale solo per quello che ha scritto una persona: un errore tecnico
                  non si "prende in carico", si corregge e sparisce da solo. */}
              {segnale.source === "feedback" && (
                <button
                  type="button"
                  className="button button--ghost button--small"
                  onClick={() => segna.mutate({ noteId: segnale.id, handled: !segnale.handledAt })}
                >
                  {segnale.handledAt
                    ? <><Undo2 size={15} aria-hidden="true" /> Rimetti fra quelle da guardare</>
                    : <><Check size={15} aria-hidden="true" /> Presa in carico</>}
                </button>
              )}
              {segnale.handledAt && (
                <small className="segnale__presa">Presa in carico il {new Date(segnale.handledAt).toLocaleDateString("it-IT")}</small>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section className="azienda-messaggi__blocco" aria-label={`Mandati a ${azienda.name}`}>
        <h4>Cosa e stato mandato a loro</h4>
        {!messaggi.isLoading && messaggi.error && (
          <ErrorState cosa="i messaggi mandati" dettaglio={messaggi.error.message} onRiprova={() => void messaggi.refetch()} />
        )}
        {messaggi.isSuccess && !messaggi.error && inviati.length === 0 && (
          <EmptyState title="Nessun messaggio mandato">
            A questa azienda non e ancora stato scritto niente dalla console.
          </EmptyState>
        )}
        <ul className="segnali-elenco">
          {inviati.map((messaggio) => (
            <li className="segnale segnale--avviso" key={messaggio.id}>
              <div className="segnale__testa">
                <Megaphone size={16} aria-hidden="true" />
                <strong>{messaggio.title}</strong>
                <small>{quando(messaggio.createdAt)}</small>
              </div>
              <p>{messaggio.body}</p>
              <small>
                {messaggio.target === "all" ? "Mandato a tutte le aziende · " : ""}
                letto da {messaggio.letti} su {messaggio.destinatari}
                {messaggio.withdrawnAt ? " · ritirato" : ""}
              </small>
              <div className="messaggio__stato">
                <button
                  type="button"
                  className="button button--ghost button--small"
                  onClick={() => setConfermeAperte(confermeAperte === messaggio.id ? null : messaggio.id)}
                >
                  <Eye size={15} aria-hidden="true" /> {confermeAperte === messaggio.id ? "Nascondi" : "Chi l'ha letto"}
                </button>
                {!messaggio.withdrawnAt && (
                  <button
                    type="button"
                    className="button button--ghost button--small"
                    onClick={() => ritira.mutate({ announcementId: messaggio.id })}
                  >
                    <Ban size={15} aria-hidden="true" /> Ritira
                  </button>
                )}
              </div>
              {confermeAperte === messaggio.id && (
                <ul className="conferme-elenco">
                  {(conferme.data ?? []).map((riga) => (
                    <li key={riga.userId}>
                      <strong>{riga.name}</strong>
                      <span>{riga.companyName}</span>
                      <small>{riga.readAt ? `letto il ${quando(riga.readAt)}` : "non ancora letto"}</small>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section className="azienda-messaggi__blocco" aria-label={`Email partite da ${azienda.name}`}>
        <h4>Email partite da loro</h4>
        {/* I tre numeri prima dell'elenco: "quante non sono partite" deve avere risposta
            senza scorrere niente, ed e la stessa promessa che aveva l'elenco generale.
            Si mostrano solo con una risposta in mano, come gli stati vuoti: fra un tentativo
            e l'altro la lettura non ha dati e non ha ancora un errore, e li il riepilogo
            scriveva tre zeri. Uno zero e piu insidioso di un elenco vuoto, perche e un
            numero, e un numero sembra una misura. */}
        {posta.isSuccess && (
        <div className="posta-riepilogo" aria-label={`Riepilogo delle email di ${azienda.name}`}>
          <span className="posta-conta posta-conta--sent"><strong>{posta.data?.summary.sent ?? 0}</strong> partite</span>
          <span className="posta-conta posta-conta--preview"><strong>{posta.data?.summary.preview ?? 0}</strong> solo preparate</span>
          <span className="posta-conta posta-conta--failed"><strong>{posta.data?.summary.failed ?? 0}</strong> non partite</span>
        </div>
        )}
        {!posta.isLoading && posta.error && (
          <ErrorState cosa="le email" dettaglio={posta.error.message} onRiprova={() => void posta.refetch()} />
        )}
        {posta.isSuccess && !posta.error && (posta.data?.rows ?? []).length === 0 && (
          <EmptyState title="Nessuna email">
            Da questa azienda non e ancora partita nessuna email verso i fornitori.
          </EmptyState>
        )}
        <ul className="email-log__elenco">
          {(posta.data?.rows ?? []).map((riga) => (
            <li className={`email-log__riga email-log__riga--${riga.status}`} key={riga.id}>
              <div className="email-log__testa">
                <strong>{etichettaEmail(riga.kind)}</strong>
                <span>{riga.recipient}</span>
                <small>{quando(riga.createdAt)}</small>
              </div>
              <span>{riga.subject}</span>
              {riga.error && <small className="email-log__errore">Non partita: {riga.error}</small>}
              {riga.status === "sent" && (
                <small className={`email-log__esito email-log__esito--${riga.outcome}`}>
                  {esitoLeggibile(riga.outcome, riga.outcomeDetail)}
                </small>
              )}
              {riga.status === "preview" && (
                <small>Preparata e mostrata, non spedita: manca la chiave del servizio di posta.</small>
              )}
              <button
                type="button"
                className="button button--ghost button--small"
                onClick={() => setPostaAperta(postaAperta === riga.id ? null : riga.id)}
              >
                <Mail size={15} aria-hidden="true" /> {postaAperta === riga.id ? "Nascondi il messaggio" : "Cosa gli e arrivato"}
              </button>
              {postaAperta === riga.id && <pre className="email-log__corpo">{riga.body}</pre>}
            </li>
          ))}
        </ul>
      </section>

      {/* Solo gli errori delle **azioni**: li il messaggio del server dice qualcosa di vero a
          chi ha appena premuto un tasto. Le letture fallite le raccontano i blocchi, ognuno
          il suo, perche dire "qualcosa non ha funzionato" senza dire cosa non aiuta nessuno. */}
      {(segna.error || ritira.error) && (
        <p className="form-error">{(segna.error ?? ritira.error)?.message}</p>
      )}
    </div>
  );
}
